import React, { useEffect, useRef } from 'react';
import { BiChevronRight, BiChevronLeft } from 'react-icons/bi';
import ProductCard, { ProductCardSkeleton } from './ProductCard';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts } from '../../store/slices/productSlice';
import { RootState, AppDispatch } from '../../store';

const RecentlyViewed = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { products, loading } = useSelector((state: RootState) => state.product);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Fetching products for recently viewed
    useEffect(() => {
        dispatch(fetchProducts({ limit: 12 }));
    }, [dispatch]);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const { current } = scrollContainerRef;
            const scrollAmount = current.clientWidth * 0.8;
            if (direction === 'left') {
                current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
            } else {
                current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            }
        }
    };

    if (!loading && (!products || products.length === 0)) {
        return null;
    }

    const displayProducts = products || [];

    return (
        <div className="recently-viewed-container">
            <style>
                {`
                .recently-viewed-container {
                    max-width: 1600px;
                    margin: 0 auto;
                    padding: 32px 40px;
                    box-sizing: border-box;
                }
                .recently-viewed-card-wrapper {
                    min-width: 260px;
                    scroll-snap-align: start;
                }
                
                @media (max-width: 768px) {
                    .recently-viewed-container {
                        padding: 20px 14px;
                    }
                    .recently-viewed-header {
                        display: flex !important;
                        flex-direction: row !important;
                        justify-content: space-between !important;
                        align-items: center !important;
                        margin-bottom: 20px !important;
                        padding-bottom: 12px !important;
                    }
                    .recently-viewed-title-container h2 {
                        font-size: 18px !important;
                    }
                    .recently-viewed-actions {
                        display: flex !important;
                        gap: 8px !important;
                    }
                    .recently-viewed-btn {
                        width: 32px !important;
                        height: 32px !important;
                        background-color: #f1f3f6 !important;
                        display: flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                    }
                    .recently-viewed-btn svg {
                        font-size: 18px !important;
                    }
                    .recently-viewed-card-wrapper {
                        min-width: 200px !important;
                    }
                }

                @media (max-width: 480px) {
                    .recently-viewed-container {
                        padding: 16px 10px;
                    }
                    .recently-viewed-title-container h2 {
                        font-size: 16px !important;
                    }
                    .recently-viewed-btn {
                        width: 28px !important;
                        height: 28px !important;
                    }
                    .recently-viewed-btn svg {
                        font-size: 16px !important;
                    }
                    .recently-viewed-card-wrapper {
                        min-width: 160px !important;
                    }
                }

                .hide-scrollbar {
                    scrollbar-width: none;
                }
                .hide-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .recently-viewed-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                    border-bottom: 1px solid #f0f0f0;
                    padding-bottom: 15px;
                }
                .recently-viewed-title-container {
                    position: relative;
                }
                .recently-viewed-title-container::after {
                    content: '';
                    position: absolute;
                    bottom: -16px;
                    left: 0;
                    width: 70px;
                    height: 3px;
                    background-color: #43c8ca;
                    border-radius: 2px;
                }
                .recently-viewed-actions {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .recently-viewed-btn {
                    background-color: #f1f3f6;
                    color: #212121;
                    border: none;
                    border-radius: 50%;
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }
                .recently-viewed-btn:hover { background-color: #e5e7eb; }
                `}
            </style>

            <div className="recently-viewed-header">
                <div className="recently-viewed-title-container">
                    <h2 style={{ fontSize: "22px", color: "#212121", fontWeight: "700", margin: 0 }}>Recently Viewed</h2>
                </div>
                <div className="recently-viewed-actions">
                    <button className="recently-viewed-btn" aria-label="Previous" onClick={() => scroll('left')}>
                        <BiChevronLeft fontSize="24px" />
                    </button>
                    <button className="recently-viewed-btn" aria-label="Next" onClick={() => scroll('right')}>
                        <BiChevronRight fontSize="24px" />
                    </button>
                </div>
            </div>

            <div
                ref={scrollContainerRef}
                style={{
                    display: "flex",
                    gap: "16px",
                    overflowX: "auto",
                    paddingBottom: "16px",
                    scrollBehavior: "smooth",
                    scrollSnapType: "x mandatory"
                }}
                className="hide-scrollbar"
            >
                {loading ? (
                    Array(6).fill(0).map((_, i) => (
                        <div key={i} className="recently-viewed-card-wrapper">
                            <ProductCardSkeleton />
                        </div>
                    ))
                ) : (
                    displayProducts.map((product: any) => {
                        const off = product.mrp && product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 15;
                        return (
                            <div key={product._id} className="recently-viewed-card-wrapper">
                                <ProductCard
                                    _id={product._id}
                                    id={product.id || product._id}
                                    image={product.images && product.images.length > 0 ? (typeof product.images[0] === 'string' ? product.images[0] : (product.images[0] as any)?.url || '') : ''}
                                    images={product.images && product.images.map((img: any) => typeof img === 'string' ? img : (img as any)?.url).filter(Boolean)}
                                    variants={product.variants}
                                    name={product.name}
                                    star={4.2}
                                    price={product.price}
                                    originalPrice={product.mrp}
                                    off={`${off}%`}
                                    isHotDeal={true}
                                    is_featured={product.is_featured}
                                    is_new={product.is_new}
                                    hideAddToCart={true}
                                />
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default RecentlyViewed;
